import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { Formik } from "formik";
import Dropdown, { DropdownValue } from "../../../components/misc/Dropdown";
import FormButtons from "../../../components/misc/FormButtons";

import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import {
  fetchAssignDriverToNewRoute,
  fetchRoutesNomenclatures,
  fetchUniqueDrivers,
} from "../../../redux/api/transport.api";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../helpers/apiHelper";
import { dateAndTimeForDatabase } from "../../../utils/dateAndTime";
import ContentCard from "../../../components/misc/ContentCard";
import DateTimePicker from "../../../components/misc/DateTimePicker";
import moment from "moment";
import { fetchFleet } from "../../../redux/api/fleet.api";

interface initialValuesInterface {
  sofer_data_start: string | null;
  sofer_data_stop: string | null;
  selectedRoute: DropdownValue | null;
  selectedCar: DropdownValue | null;
  selectedDriver: DropdownValue | null;
}

const initialValues: initialValuesInterface = {
  sofer_data_start: null,
  sofer_data_stop: null,
  selectedRoute: null,
  selectedCar: null,
  selectedDriver: null,
};

const formikSchema = Yup.object({
  sofer_data_start: Yup.date()
    .nullable()
    .typeError("Data inceput nu este validă")
    .required("Data inceput nu poate fi goală")
    .test(
      "is-greater",
      "Data start trebuie să fie mai mica decat data stop",
      function (value) {
        const { sofer_data_stop } = this.parent;
        return (
          !value ||
          !sofer_data_stop ||
          new Date(value) < new Date(sofer_data_stop)
        );
      },
    ),

  sofer_data_stop: Yup.date()
    .nullable()
    .typeError("Data stop nu este validă")
    .required("Data stop nu poate fi goală")
    .test(
      "is-lesser",
      "Data stop trebuie să fie mai mare decat data start",
      function (value) {
        const { sofer_data_start } = this.parent;
        return (
          !value ||
          !sofer_data_start ||
          new Date(value) > new Date(sofer_data_start)
        );
      },
    ),
  selectedRoute: Yup.object().required("Va rugam selectati ruta").nullable(),
  selectedCar: Yup.object().required("Va rugam selectati masina").nullable(),
  selectedDriver: Yup.object().required("Va rugam selectati sofer").nullable(),
});

function AssignDriverRoute() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [formikValues, setFormikValues] =
    useState<initialValuesInterface>(initialValues);
  const [isSaving, setIsSaving] = useState(false);

  const {
    transport: {
      uniqueDrivers: {
        data: { data: uniqueDrivers },
        isLoading: isLoadingUniqueDrivers,
      },
      routesNomenclatures: {
        data: { data: routesNomenclatures },
        isLoading: isLoadingRoutesNomenclatures,
      },
      assignedDriverData,
    },
    fleet: {
      fleetData: {
        data: { data: fleetData },
        isLoading: isLoadingFleetData,
      },
    },
  } = useAppSelector((state) => ({
    transport: state.transport,
    expedition: state.expedition,
    fleet: state.fleet,
  }));

  useEffect(() => {
    dispatch(
      fetchUniqueDrivers({
        perPage: 10000,
        routeType: "local",
      }),
    );
    dispatch(
      fetchRoutesNomenclatures({
        perPage: 10000,
      }),
    );
    dispatch(
      fetchFleet({
        perPage: 10000,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (assignedDriverData) {
      if (
        uniqueDrivers.length > 0 &&
        routesNomenclatures.length > 0 &&
        fleetData.length > 0
      ) {
        const selectedDriver = uniqueDrivers?.find(
          (driver) => driver.id_user === assignedDriverData.id_user,
        );
        const selectedRoute = routesNomenclatures?.find(
          (route) => route.id_ruta === assignedDriverData.id_ruta,
        );
        const selectedCar = fleetData?.find(
          (car) => car.nrauto === assignedDriverData.nrauto,
        );
        setFormikValues((prev) => {
          return {
            ...prev,
            selectedRoute: selectedRoute
              ? {
                  label: selectedRoute.denumire,
                  id: selectedRoute.id_ruta,
                }
              : null,
            selectedCar: selectedCar
              ? {
                  label: selectedCar.nrauto,
                  id: selectedCar.id_auto,
                }
              : null,
            selectedDriver: selectedDriver
              ? {
                  label: `${selectedDriver.nume} ${selectedDriver.prenume}`,
                  id: selectedDriver.id_user,
                }
              : null,
          };
        });
      }
    }
  }, [
    assignedDriverData,
    dispatch,
    fleetData,
    routesNomenclatures,
    uniqueDrivers,
  ]);

  const handleSave = async (value: initialValuesInterface) => {
    setIsSaving(true);

    const resultAction = await dispatch(
      fetchAssignDriverToNewRoute({
        ...value,
        id_user: value.selectedDriver?.id,
        id_ruta: value.selectedRoute?.id,
        id_auto: value.selectedCar?.id,
        sofer_data_start: dateAndTimeForDatabase(value.sofer_data_start),
        sofer_data_stop: dateAndTimeForDatabase(value.sofer_data_stop),
      }),
    );

    if (
      checkEndpointStatus(resultAction, fetchAssignDriverToNewRoute) !==
      endpointStatus.pending
    ) {
      setIsSaving(false);
    }

    if (
      checkEndpointStatus(resultAction, fetchAssignDriverToNewRoute) ===
      endpointStatus.fulfilled
    ) {
      navigate(-1);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const driversNomenclaturesDropdownValues = useMemo(
    () =>
      uniqueDrivers?.map((item) => ({
        label: `${item.nume} ${item.prenume}`,
        id: item.id_user,
      })),
    [uniqueDrivers],
  );

  const routesNomenclaturesDropdownValues = useMemo(
    () =>
      routesNomenclatures?.map((item) => ({
        label: item.denumire,
        id: item.id_ruta,
      })),
    [routesNomenclatures],
  );

  const fleetNomenclaturesDropdownValues = useMemo(
    () =>
      fleetData?.map((item) => ({
        label: item.nrauto,
        id: item.id_auto,
      })),
    [fleetData],
  );

  const sortedRoutesNomenclaturesDropdownValues =
    routesNomenclaturesDropdownValues.sort((a, b) => {
      if (a.label.toLowerCase() < b.label.toLowerCase()) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });

  return (
    <ContentCard
      isLoading={
        isLoadingRoutesNomenclatures ||
        isLoadingFleetData ||
        isLoadingUniqueDrivers
      }
      withShadow={false}
      withPadding={false}>
      <Formik
        enableReinitialize
        initialValues={formikValues}
        validationSchema={formikSchema}
        onSubmit={handleSave}>
        {({
          values,
          isSubmitting,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
        }) => {
          const { selectedRoute, selectedCar, selectedDriver } = values;

          const isStartAfterStop = moment(values.sofer_data_start).isAfter(
            moment(values.sofer_data_stop),
          );

          return (
            <form
              className="w-full"
              onSubmit={handleSubmit}
              noValidate
              id="kt_login_signin_form">
              <Dropdown
                className="my-4"
                title={"Selecteaza sofer"}
                data={driversNomenclaturesDropdownValues}
                singleValue={selectedDriver}
                setSingleValue={(value) => {
                  setFieldValue("selectedDriver", value);
                }}
                error={errors["selectedDriver"]}
                touched={touched["selectedDriver"]}
              />
              <div className="block gap-2 md:flex">
                <DateTimePicker
                  label="Data incepere valabilitate"
                  minDate={new Date()}
                  value={values.sofer_data_start}
                  handleChangeDate={(newDate) => {
                    setFieldValue("sofer_data_start", newDate);
                  }}
                  error={errors["sofer_data_start"]}
                  touched={touched["sofer_data_start"]}
                />

                <DateTimePicker
                  label="Data sfarsit valabilitate"
                  minDate={new Date()}
                  value={values.sofer_data_stop}
                  handleChangeDate={(newDate) => {
                    setFieldValue("sofer_data_stop", newDate);
                  }}
                  error={errors["sofer_data_stop"]}
                  touched={touched["sofer_data_stop"]}
                  withVerticalSpacer
                />
              </div>

              <Dropdown
                className="my-4"
                title={"Selecteaza ruta locala"}
                data={sortedRoutesNomenclaturesDropdownValues}
                singleValue={selectedRoute}
                setSingleValue={(value) => {
                  setFieldValue("selectedRoute", value);
                }}
                error={errors["selectedRoute"]}
                touched={touched["selectedRoute"]}
              />
              <Dropdown
                className="my-4"
                title={"Selecteaza masina"}
                data={fleetNomenclaturesDropdownValues}
                singleValue={selectedCar}
                setSingleValue={(value) => {
                  setFieldValue("selectedCar", value);
                }}
                error={errors["selectedCar"]}
                touched={touched["selectedCar"]}
              />

              <FormButtons
                goBackLabel="Anuleaza"
                goNextLabel="Salveaza"
                handleGoBack={handleCancel}
                nextButtonDisabled={isSubmitting || isStartAfterStop}
                nextButtonLoading={isSaving}
              />
            </form>
          );
        }}
      </Formik>
    </ContentCard>
  );
}

export default AssignDriverRoute;
